<template>
  <NamiLoading v-if="pending" />
  <div v-else-if="data" class="grid gap-4">
    <TitleListCard
      v-for="list in data.data"
      v-show="!list.list.attributes.deletedAt"
      :title-list="list.list"
      :titles="list.titles"
      :key="list.list.id"
      @deleted="hideDeletedList(list.list.id)"
      hide-user
    />
    <NoResults
      v-if="!data.data.length"
      class="col-span-full text-center tile"
      message="No Lists"
    />
    <NamiPagination
      v-model="page"
      :total-pages="Math.ceil((data?.meta.total ?? 0) / limit)"
      keep-route
    />
  </div>
</template>

<script setup lang="ts">
import type { UserEntity } from "~/src/api";
import {
  ALL_CONTENT,
  isTitle,
  Title,
  type TitleEntity,
  TitleList,
} from "~/src/api";

const props = defineProps<{
  user: UserEntity;
}>();

const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();

const { limit, offset, page } = usePagination();

// it seems the BE limit is 20, use 16 to be able to show if there are more titles
const PREVIEW_TITLES = 16;

const { data, pending } = useAsyncData(
  async () => {
    await authStore?.waitUntilRefreshIsComplete();
    const token = (await authStore?.getToken()) || undefined;

    const res = await TitleList.find(
      {
        userId: props.user.id,
        limit: limit.value,
        offset: offset.value,
        showDeleted: false,
      },
      token,
    );

    const displayedTitleIds = res.data.flatMap((item) =>
      item.relationships
        .filter(isTitle)
        .slice(0, PREVIEW_TITLES)
        .map((rel) => rel.id),
    );
    const uniqueTitleIds = Array.from(new Set(displayedTitleIds));

    const titleMap: { [key in string]: TitleEntity } = {};

    if (uniqueTitleIds.length > 0) {
      const groups = uniqueTitleIds.group(100);

      const titles = await Promise.all(
        groups.map((ids) =>
          Title.search({
            ids,
            includes: ["cover_art"],
            contentRating: ALL_CONTENT,
          }).then((res) => res.data),
        ),
      );

      titles.flat().forEach((title) => {
        titleMap[title.id] = title;
      });
    }

    return {
      ...res,
      data: res.data.map((list) => ({
        list,
        titles: list.relationships
          .filter(isTitle)
          .slice(0, PREVIEW_TITLES)
          .map((rel) => titleMap[rel.id])
          .filter(onlyTruthys),
      })),
    };
  },
  { server: false, watch: [page] },
);

const hideDeletedList = (id: string) => {
  if (data.value)
    data.value.data =
      data.value.data.filter(({ list }) => list.id !== id) ?? [];
};
</script>
